import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import {
  pgEnum,
  pgTable,
  uuid,
  integer,
  boolean,
  index,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas';

/**
 * Enum defining the possible billing frequencies for a subscription.
 * - DAILY: Bill every X days
 * - WEEKLY: Bill every X weeks
 * - MONTHLY: Bill every X months
 * - YEARLY: Bill every X years
 */
export const billing_frequency = pgEnum('billing_frequency', [
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'YEARLY',
]);

/**
 * Enum defining the billing cycle anchor options for a subscription.
 * - SERVICE_DATE: Anchor to the date of service (typically the delivery date)
 * - FIRST_OF_MONTH: Anchor to the 1st day of the month
 * - LAST_DAY_OF_MONTH: Anchor to the last day of the month
 * - CUSTOM_DAY_OF_MONTH: Anchor to specific days of the month
 * - CUSTOM_DAY_OF_WEEK: Anchor to specific days of the week
 * - FIRST_OF_QUARTER: Anchor to the 1st day of the quarter
 */
export const billing_cycle_anchor = pgEnum('billing_cycle_anchor', [
  'SERVICE_DATE',
  'FIRST_OF_MONTH',
  'LAST_DAY_OF_MONTH',
  'CUSTOM_DAY_OF_MONTH',
  'CUSTOM_DAY_OF_WEEK',
  'FIRST_OF_QUARTER',
]);

/**
 * Enum defining the billing preference options.
 * - FORWARD: Bill in advance (before the service period)
 * - BACKWARD: Bill in arrears (after the service period)
 */
export const billing_preference = pgEnum('billing_preference', [
  'FORWARD',
  'BACKWARD',
]);

export const proration_method = pgEnum('proration_method', [
  'SERVICE_BASED',
  'PERIOD_BASED',
]);

/**
 * How PERIOD_BASED proration prices a partial period (ENG-8129).
 * - ACTUAL_DAYS: billable days ÷ days in the period
 * - FIXED_28: billable days ÷ 28, capped at 100%
 * - FIXED_28_WEEKS: each started week = 25%, capped at 100%
 */
export const period_proration_basis = pgEnum('period_proration_basis', [
  'ACTUAL_DAYS',
  'FIXED_28',
  'FIXED_28_WEEKS',
]);

export const subscription_settings_version = pgEnum(
  'subscription_settings_version',
  ['V1', 'V2'],
);

/**
 * Represents the billing configuration settings for a subscription.
 * These settings define how often a subscription is billed, when billing occurs,
 * and how partial periods are handled.
 */
export const subscription_settings = pgTable(
  'subscription_settings',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** Version marker for legacy vs new configurations */
    version: subscription_settings_version('version').default('V1').notNull(),
    /** Reference to the SKU this setting applies to */
    skuId: uuid('skuId'),
    /** What date the billing cycle is anchored to */
    billingCycleAnchor: billing_cycle_anchor('billingCycleAnchor')
      .notNull()
      .default('SERVICE_DATE'),
    /** Custom days of month (1-31) for custom_day_of_month anchor type */
    customAnchorDay: integer('customAnchorDay').array(),
    /** Custom days of week (0=Sunday, 6=Saturday) for custom_day_of_week anchor type */
    customAnchorDaysOfWeek: integer('customAnchorDaysOfWeek').array(),
    /** How often billing occurs (daily, weekly, monthly, yearly) */
    frequency: billing_frequency('frequency').notNull().default('MONTHLY'),
    /** Time interval for the frequency (e.g., every 2 weeks, every 3 months) */
    interval: integer('interval').notNull().default(1),
    /** Whether to bill in advance or arrears */
    billingPreference: billing_preference('billingPreference')
      .notNull()
      .default('FORWARD'),
    /** Whether to prorate charges for partial periods */
    proration: boolean('proration').notNull().default(true),
    /** Whether to prorate on installation */
    prorateOnInstallation: boolean('prorateOnInstallation')
      .notNull()
      .default(true),
    /** Whether to prorate on removal */
    prorateOnRemoval: boolean('prorateOnRemoval')
      .notNull()
      .default(false),
    /** Who last modified the subscription settings */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** Who created the subscription settings */
    createdBy: uuid('createdBy'),
    /** Optional name for these settings */
    name: varchar('name', { length: 255 }),
    /** Optional description for these settings */
    description: varchar('description', { length: 1000 }),
    /** How to prorate the charge */
    prorationMethod: proration_method('prorationMethod'),
    /** Optional months (1-12) when billing is active. Null/empty = all months. */
    activeMonths: integer('activeMonths').array(),
    /** How PERIOD_BASED proration prices a partial period (actual days, 28 days exact, 28 days by weeks) */
    periodProrationBasis: period_proration_basis('periodProrationBasis').notNull().default('ACTUAL_DAYS'),
    /** Minimum rental length in days; shortfall is billed on the final invoice */
    minimumRentalDays: integer('minimumRentalDays'),
  },
  (table) => ({
    frequencyIdx: index('subscription_settings_frequency_idx').on(
      table.frequency,
    ),
    anchorIdx: index('subscription_settings_anchor_idx').on(
      table.billingCycleAnchor,
    ),
    skuIdIdx: index('subscription_settings_skuId_idx').on(table.skuId),
  }),
);

/**
 * History table for tracking changes to subscription settings.
 */
export const subscription_settings_history = pgTable(
  'subscription_settings_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    version: subscription_settings_version('version'),
    /** Reference to the SKU this setting applies to (nullable for reusable templates) */
    skuId: uuid('skuId'),
    /** What date the billing cycle is anchored to */
    billingCycleAnchor: billing_cycle_anchor('billingCycleAnchor').notNull(),
    /** Custom days of month (1-31) for custom_day_of_month anchor type */
    customAnchorDay: integer('customAnchorDay').array(),
    /** Custom days of week (0=Sunday, 6=Saturday) for custom_day_of_week anchor type */
    customAnchorDaysOfWeek: integer('customAnchorDaysOfWeek').array(),
    /** How often billing occurs (daily, weekly, monthly, yearly) */
    frequency: billing_frequency('frequency').notNull(),
    /** Time interval for the frequency (e.g., every 2 weeks, every 3 months) */
    interval: integer('interval').notNull(),
    /** Whether to bill in advance or arrears */
    billingPreference: billing_preference('billingPreference').notNull(),
    /** Whether to prorate charges for partial periods */
    proration: boolean('proration').notNull(),
    /** Whether to prorate on installation */
    prorateOnInstallation: boolean('prorateOnInstallation')
      .notNull()
      .default(true),
    /** Whether to prorate on removal */
    prorateOnRemoval: boolean('prorateOnRemoval')
      .notNull()
      .default(false),
    /** Who last modified the subscription settings */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** Who created the subscription settings */
    createdBy: uuid('createdBy'),
    /** Optional name for these settings */
    name: varchar('name', { length: 255 }),
    /** Optional description for these settings */
    description: varchar('description', { length: 1000 }),
    /** How to prorate the charge */
    prorationMethod: proration_method('prorationMethod'),
    /** Optional months (1-12) when billing is active. Null/empty = all months. */
    activeMonths: integer('activeMonths').array(),
    /** How PERIOD_BASED proration prices a partial period (actual days, 28 days exact, 28 days by weeks) */
    periodProrationBasis: period_proration_basis('periodProrationBasis'),
    /** Minimum rental length in days; shortfall is billed on the final invoice */
    minimumRentalDays: integer('minimumRentalDays'),
  },
);

export type SubscriptionSettingsSchema =
  typeof subscription_settings.$inferSelect;
